import { Measure } from '../types'
import Big from 'big.js'

enum VolumeMeasurementSystem {
  metric = 'Metric Units',
  usCustomary = 'US Customary Units',
  imperial = 'Imperial Units'
}

const volumeUnits = {
  cubic_millimeter: {
    displayName: 'Cubic millimeter',
    codeName: 'cubic_millimeter',
    url: 'cubic-millimeter',
    symbol: 'mm³',
    toBaseUnit: (value: Big) => value.times(0.000001),
    fromBaseUnit: (value: Big) => value.div(0.000001),
    system: VolumeMeasurementSystem.metric
  },
  cubic_centimeter: {
    displayName: 'Cubic centimeter',
    codeName: 'cubic_centimeter',
    url: 'cubic-centimeter',
    symbol: 'cm³',
    toBaseUnit: (value: Big) => value.times(0.001),
    fromBaseUnit: (value: Big) => value.div(0.001),
    system: VolumeMeasurementSystem.metric
  },
  cubic_decimeter: {
    displayName: 'Cubic decimeter',
    codeName: 'cubic_decimeter',
    url: 'cubic-decimeter',
    symbol: 'dm³',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: VolumeMeasurementSystem.metric
  },
  cubic_meter: {
    displayName: 'Cubic meter',
    codeName: 'cubic_meter',
    url: 'cubic-meter',
    symbol: 'm³',
    toBaseUnit: (value: Big) => value.times(1000),
    fromBaseUnit: (value: Big) => value.div(1000),
    system: VolumeMeasurementSystem.metric
  },
  cubic_decameter: {
    displayName: 'Cubic decameter',
    codeName: 'cubic_decameter',
    url: 'cubic-decameter',
    symbol: 'dam³',
    toBaseUnit: (value: Big) => value.times(1000000),
    fromBaseUnit: (value: Big) => value.div(1000000),
    system: VolumeMeasurementSystem.metric
  },
  cubic_hectometer: {
    displayName: 'Cubic hectometer',
    codeName: 'cubic_hectometer',
    url: 'cubic-hectometer',
    symbol: 'hm³',
    toBaseUnit: (value: Big) => value.times(1000000000),
    fromBaseUnit: (value: Big) => value.div(1000000000),
    system: VolumeMeasurementSystem.metric
  },
  cubic_kilometer: {
    displayName: 'Cubic kilometer',
    codeName: 'cubic_kilometer',
    url: 'cubic-kilometer',
    symbol: 'km³',
    toBaseUnit: (value: Big) => value.times(1000000000000),
    fromBaseUnit: (value: Big) => value.div(1000000000000),
    system: VolumeMeasurementSystem.metric
  },
  milliliter: {
    displayName: 'Milliliter',
    codeName: 'milliliter',
    url: 'milliliter',
    symbol: 'ml',
    toBaseUnit: (value: Big) => value.div(1000),
    fromBaseUnit: (value: Big) => value.times(1000),
    system: VolumeMeasurementSystem.metric
  },
  centiliter: {
    displayName: 'Centiliter',
    codeName: 'centiliter',
    url: 'centiliter',
    symbol: 'cl',
    toBaseUnit: (value: Big) => value.div(100),
    fromBaseUnit: (value: Big) => value.times(100),
    system: VolumeMeasurementSystem.metric
  },
  deciliter: {
    displayName: 'Deciliter',
    codeName: 'deciliter',
    url: 'deciliter',
    symbol: 'dl',
    toBaseUnit: (value: Big) => value.div(10),
    fromBaseUnit: (value: Big) => value.times(10),
    system: VolumeMeasurementSystem.metric
  },
  liter: {
    displayName: 'Liter',
    codeName: 'liter',
    url: 'liter',
    symbol: 'l',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: VolumeMeasurementSystem.metric
  },
  decaliter: {
    displayName: 'Decaliter',
    codeName: 'decaliter',
    url: 'decaliter',
    symbol: 'dal',
    toBaseUnit: (value: Big) => value.times(10),
    fromBaseUnit: (value: Big) => value.div(10),
    system: VolumeMeasurementSystem.metric
  },
  hectoliter: {
    displayName: 'Hectoliter',
    codeName: 'hectoliter',
    url: 'hectoliter',
    symbol: 'hl',
    toBaseUnit: (value: Big) => value.times(100),
    fromBaseUnit: (value: Big) => value.div(100),
    system: VolumeMeasurementSystem.metric
  },
  kiloliter: {
    displayName: 'Kiloliter',
    codeName: 'kiloliter',
    url: 'kiloliter',
    symbol: 'kl',
    toBaseUnit: (value: Big) => value.times(1000),
    fromBaseUnit: (value: Big) => value.div(1000),
    system: VolumeMeasurementSystem.metric
  },


  cubic_foot: {
    displayName: 'Cubic foot',
    codeName: 'cubic_foot',
    url: 'cubic-foot',
    symbol: 'ft³',
    toBaseUnit: (value: Big) => value.times(28.3168465923398),
    fromBaseUnit: (value: Big) => value.div(28.3168465923398),
    system: VolumeMeasurementSystem.usCustomary
  },
  cubic_inch: {
    displayName: 'Cubic inch',
    codeName: 'cubic_inch',
    url: 'cubic-inch',
    symbol: 'in³',
    toBaseUnit: (value: Big) => value.times(0.016387064),
    fromBaseUnit: (value: Big) => value.div(0.016387064),
    system: VolumeMeasurementSystem.usCustomary
  },
  cubic_yard: {
    displayName: 'Cubic yard',
    codeName: 'cubic_yard',
    url: 'cubic-yard',
    symbol: 'in³',
    toBaseUnit: (value: Big) => value.times(0.00130795061932262),
    fromBaseUnit: (value: Big) => value.div(0.00130795061932262),
    system: VolumeMeasurementSystem.usCustomary
  },
  cubic_mile: {
    displayName: 'Cubic mile',
    codeName: 'cubic_mile',
    url: 'cubic-mile',
    symbol: 'in³',
    toBaseUnit: (value: Big) => value.times(2.39912758578928E-13),
    fromBaseUnit: (value: Big) => value.div(2.39912758578928E-13),
    system: VolumeMeasurementSystem.usCustomary
  },
  us_teaspoon: {
    displayName: 'US teaspoon',
    codeName: 'us_teaspoon',
    url: 'us-teaspoon',
    symbol: 'tsp',
    toBaseUnit: (value: Big) => value.times(0.0049289215937757),
    fromBaseUnit: (value: Big) => value.div(0.0049289215937757),
    system: VolumeMeasurementSystem.usCustomary
  },
  us_tablespoon: {
    displayName: 'US tablespoon',
    codeName: 'us_tablespoon',
    url: 'us-tablespoon',
    symbol: 'tbsp',
    toBaseUnit: (value: Big) => value.times(0.0147867647811813),
    fromBaseUnit: (value: Big) => value.div(0.0147867647811813),
    system: VolumeMeasurementSystem.usCustomary
  },
  us_fluid_ounce: {
    displayName: 'US fluid ounce',
    codeName: 'us_fluid_ounce',
    url: 'us-fluid-ounce',
    symbol: 'us fl oz',
    toBaseUnit: (value: Big) => value.times(0.0295735295623627),
    fromBaseUnit: (value: Big) => value.div(0.0295735295623627),
    system: VolumeMeasurementSystem.usCustomary
  },
  us_legal_cup: {
    displayName: 'US legal cup',
    codeName: 'us_legal_cup',
    url: 'us-legal-cup',
    symbol: 'cup',
    toBaseUnit: (value: Big) => value.times(0.240),
    fromBaseUnit: (value: Big) => value.div(0.240),
    system: VolumeMeasurementSystem.usCustomary
  },
  us_liquid_pint: {
    displayName: 'US liquid pint',
    codeName: 'us_liquid_pint',
    url: 'us-liquid-pint',
    symbol: 'pt',
    toBaseUnit: (value: Big) => value.div(2.1133764188652),
    fromBaseUnit: (value: Big) => value.times(2.1133764188652),
    system: VolumeMeasurementSystem.usCustomary
  },
  us_liquid_quart: {
    displayName: 'US liquid quart',
    codeName: 'us_liquid_quart',
    url: 'us-liquid-quart',
    symbol: 'qt',
    toBaseUnit: (value: Big) => value.div(1.0566882094),
    fromBaseUnit: (value: Big) => value.times(1.0566882094),
    system: VolumeMeasurementSystem.usCustomary
  },
  us_liquid_gallon: {
    displayName: 'US liquid gallon',
    codeName: 'us_liquid_gallon',
    url: 'us-liquid-gallon',
    symbol: 'gal',
    toBaseUnit: (value: Big) => value.times(3.785411784),
    fromBaseUnit: (value: Big) => value.div(3.785411784),
    system: VolumeMeasurementSystem.usCustomary
  },


  imperial_gallon: {
    displayName: 'Imperial gallon',
    codeName: 'imperial_gallon',
    url: 'imperial-gallon',
    symbol: 'imp gal',
    toBaseUnit: (value: Big) => value.times(4.54608999998115),
    fromBaseUnit: (value: Big) => value.div(4.54608999998115),
    system: VolumeMeasurementSystem.imperial
  },
  imperial_quart: {
    displayName: 'Imperial quart',
    codeName: 'imperial_quart',
    url: 'imperial-quart',
    symbol: 'imp qt',
    toBaseUnit: (value: Big) => value.times(1.13652249999529),
    fromBaseUnit: (value: Big) => value.div(1.13652249999529),
    system: VolumeMeasurementSystem.imperial
  },
  imperial_pint: {
    displayName: 'Imperial pint',
    codeName: 'imperial_pint',
    url: 'imperial-pint',
    symbol: 'imp pt',
    toBaseUnit: (value: Big) => value.times(0.56826125),
    fromBaseUnit: (value: Big) => value.div(0.56826125),
    system: VolumeMeasurementSystem.imperial
  },
  imperial_cup: {
    displayName: 'Imperial cup',
    codeName: 'imperial_cup',
    url: 'imperial-cup',
    symbol: 'imp cup',
    toBaseUnit: (value: Big) => value.times(0.284130624998822),
    fromBaseUnit: (value: Big) => value.div(0.284130624998822),
    system: VolumeMeasurementSystem.imperial
  },
  imp_fluid_ounce: {
    displayName: 'Imperial fluid ounce',
    codeName: 'imp_fluid_ounce',
    url: 'imperial-fluid-ounce',
    symbol: 'imp fl oz',
    toBaseUnit: (value: Big) => value.times(0.0284130624998822),
    fromBaseUnit: (value: Big) => value.div(0.0284130624998822),
    system: VolumeMeasurementSystem.imperial
  },
  imperial_tablespoon: {
    displayName: 'Imperial tablespoon',
    codeName: 'imperial_tablespoon',
    url: 'imperial-tablespoon',
    symbol: 'imp tbsp',
    toBaseUnit: (value: Big) => value.times(0.0177581640623633),
    fromBaseUnit: (value: Big) => value.div(0.0177581640623633),
    system: VolumeMeasurementSystem.imperial
  },
  imperial_teaspoon: {
    displayName: 'Imperial teaspoon',
    codeName: 'imperial_teaspoon',
    url: 'imperial-teaspoon',
    symbol: 'imp tsp',
    toBaseUnit: (value: Big) => value.times(0.00591938802096296),
    fromBaseUnit: (value: Big) => value.div(0.00591938802096296),
    system: VolumeMeasurementSystem.imperial
  }
}

export const volume: Measure = {
  displayName: 'Volume',
  codeName: 'volume',
  url: 'volume',
  units: volumeUnits,
  baseUnit: volumeUnits.liter,
  defaultFromUnit: volumeUnits.cubic_meter,
  defaultToUnit: volumeUnits.liter
}
